import { FC } from 'react';
import { css } from '@emotion/react';
import { SuccessGraphic } from '@frontend/assets';
import { DigitalFormsPdfExpiredSvg } from '@frontend/digital-forms-assets';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, Heading, Text } from '@frontend/design-system';
import Footer from '../../../../layout/footer.component';
import { CountDownTimer } from '../countdown-timer';
import { SubmissionConfirmationMode } from './derive-mode';

// ---------------------------------------------------------------------------
// Props — injected by the Container; all IO is pre-resolved to plain values
// ---------------------------------------------------------------------------

export interface SubmissionConfirmationViewProps {
  mode: SubmissionConfirmationMode;
  locationId: string;
  /** Seconds remaining on the 2-minute PDF download window. */
  countdownSeconds: number;
  isCountdownComplete: boolean;
  isDownloading: boolean;
  onDownloadPdf: () => void;
  onKioskClose: () => void;
}

const cardStyle = css({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gap: theme.spacing(2),
  width: '100%',
  minHeight: 500,
  padding: theme.spacing(3, 2),
  boxSizing: 'border-box',
  backgroundColor: theme.colors.white,
  borderRadius: theme.borderRadius.medium,
  boxShadow: theme.shadows.floating,
});

// ---------------------------------------------------------------------------
// Strategy map — Open/Closed: adding a new mode = add a renderer + entry here
// ---------------------------------------------------------------------------

const RENDERERS: Record<Exclude<SubmissionConfirmationMode, undefined>, FC<SubmissionConfirmationViewProps>> = {
  kiosk: KioskSuccessView,
  preview: PreviewSuccessView,
};

// ---------------------------------------------------------------------------
// Root view
// ---------------------------------------------------------------------------

export const SubmissionConfirmationView: FC<SubmissionConfirmationViewProps> = (props) => {
  const { mode, locationId } = props;

  const Renderer = mode != null ? RENDERERS[mode] : null;
  const content = Renderer ? <Renderer {...props} /> : <DownloadWithCountdownView {...props} />;

  return (
    <>
      <div css={cardStyle}>{content}</div>
      <Footer locationId={locationId} />
    </>
  );
};

// ---------------------------------------------------------------------------
// Sub-views (each has one job — S)
// ---------------------------------------------------------------------------

function KioskSuccessView({ onKioskClose }: SubmissionConfirmationViewProps) {
  const { t } = useTranslation('digital-forms-experience');

  return (
    <>
      <SuccessGraphic css={{ maxWidth: 200 }} aria-label={t('Successful submission')} />
      <Heading level={2} textAlign='center'>
        {t('Your form has been submitted successfully!')}
      </Heading>
      <Text color='subdued' textAlign='center'>
        {t('Please contact the business if you have any questions.')}
      </Text>
      <Button onClick={onKioskClose}>{t('Close')}</Button>
    </>
  );
}

function PreviewSuccessView(_props: SubmissionConfirmationViewProps) {
  return <SuccessContent />;
}

function DownloadWithCountdownView({
  countdownSeconds,
  isCountdownComplete,
  isDownloading,
  onDownloadPdf,
}: SubmissionConfirmationViewProps) {
  const { t } = useTranslation('digital-forms-experience');

  if (isCountdownComplete) {
    return (
      <>
        <DigitalFormsPdfExpiredSvg css={{ maxWidth: 190 }} aria-label={t('PDF download expired')} />
        <Heading level={2} textAlign='center' css={{ maxWidth: 350 }}>
          {t('Download time for your submission has expired')}
        </Heading>
        <Text color='subdued' textAlign='center' css={{ maxWidth: 350 }}>
          {t('Unfortunately, we cannot allow downloads beyond the specified time limit to protect your privacy.')}
        </Text>
      </>
    );
  }

  return (
    <>
      <SuccessContent />
      <CountDownTimer timeInSeconds={countdownSeconds} />
      <Button loading={isDownloading} onClick={onDownloadPdf}>
        {t('Download Now')}
      </Button>
    </>
  );
}

/** Shared success copy used by both PreviewSuccess and DownloadWithCountdown. */
function SuccessContent() {
  const { t } = useTranslation('digital-forms-experience');

  return (
    <>
      <SuccessGraphic css={{ maxWidth: 200 }} aria-label={t('Successful submission')} />
      <Heading level={2} textAlign='center'>
        {t("You've submitted all the forms successfully!")}
      </Heading>
      <Text color='subdued' textAlign='center' css={{ maxWidth: 260 }}>
        {t('You can download a PDF of the forms that you submitted just now.')}
      </Text>
      <Text color='subdued' textAlign='center' css={{ maxWidth: 274 }}>
        {t('To protect your privacy, this download option will time out in 2 minutes.')}
      </Text>
    </>
  );
}
